import { useState, useEffect } from 'react';
import {
  ShieldCheck, Plus, ArrowLeft, Save, Trash2, Lock, Loader2,
} from 'lucide-react';
import {
  fetchRoles, fetchRole, createRole, updateRole, deleteRole,
  PERMISSION_RESOURCES,
  type RoleView,
  type PermissionMatrixEntry,
  type PermissionResource,
  type RoleTemplateValue,
} from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { roleNameForDisplay } from '@/pages/users/roleLabels';
import styles from './RolesPage.module.css';
import { PoApproversCard } from './PoApproversCard';
import { PartnerApproversCard } from './PartnerApproversCard';

const TEMPLATE_LABELS: Record<RoleTemplateValue, string> = {
  building_admin: 'Building Admin',
  charger_owner: 'Charger Owner',
  property_manager: 'Property Manager',
  // Agent commission Phase 1b — display label for the sales_agent
  // template. Exhaustive Record forces this entry once the union
  // expands in api.ts.
  sales_agent: 'Sales Partner',
};

const TEMPLATE_VALUES: RoleTemplateValue[] = [
  'building_admin',
  'charger_owner',
  'property_manager',
  'sales_agent',
];

const SYSTEM_ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  tenant_admin: 'Tenant Admin',
  operator: 'Operator',
  viewer: 'Viewer',
  third_party_admin: 'Third-Party Admin',
};

/**
 * DISPLAY-ONLY labels for permission-matrix resource keys. The keys
 * themselves are the RBAC contract (@evi/shared PERMISSION_RESOURCES,
 * mirrored in stored Role matrices and every server-side permission
 * check) and must never be renamed here — this map only changes what
 * the operator reads. Unmapped resources fall through to the raw key.
 */
const RESOURCE_LABELS: Record<string, string> = {
  // The Tenant record is surfaced as "Operator" across the admin UI —
  // it is the org container that owns locations, pays EVI, and takes
  // payouts. Here it reads "Operator account" specifically to stay
  // distinguishable from the `operator` SYSTEM ROLE above (a staff
  // permission tier WITHIN one such org), which this same page renders
  // as plain "Operator".
  Tenant: 'Operator account',
};

type Action = 'create' | 'read' | 'update' | 'delete';
const ACTIONS: Action[] = ['create', 'read', 'update', 'delete'];

interface RolesPageProps {
  onNavigate?: (path: string) => void;
}

export function RolesPage({ onNavigate }: RolesPageProps) {
  useRecentRoute('Roles & Permissions');
  const [roles, setRoles] = useState<RoleView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterTemplate, setFilterTemplate] = useState<'' | RoleTemplateValue>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      setRoles(await fetchRoles(filterTemplate || undefined));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [filterTemplate]);

  if (selectedId) {
    return (
      <RoleDetail
        roleId={selectedId}
        onBack={() => { setSelectedId(null); void load(); }}
        onDeleted={() => { setSelectedId(null); void load(); }}
      />
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><ShieldCheck size={18} /></div>
          <div>
            <h1 className={styles.title}>Roles &amp; Permissions</h1>
            <p className={styles.subtitle}>{roles.length} role{roles.length === 1 ? '' : 's'} visible</p>
          </div>
        </div>
        <button className={styles.primaryBtn} onClick={() => setCreateModalOpen(true)}>
          <Plus size={16} /> New Role
        </button>
      </div>

      <div className={styles.filterRow}>
        <select
          className={styles.select}
          value={filterTemplate}
          onChange={(e) => setFilterTemplate(e.target.value as '' | RoleTemplateValue)}
          aria-label="Filter by template"
        >
          <option value="">All templates</option>
          {TEMPLATE_VALUES.map((t) => (
            <option key={t} value={t}>{TEMPLATE_LABELS[t]}</option>
          ))}
        </select>
      </div>

      <PoApproversCard />
      <PartnerApproversCard />
      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading…</div>
      ) : (
        <div className={styles.list}>
          {roles.map((role) => (
            <button
              key={role._id}
              type="button"
              className={styles.row}
              onClick={() => setSelectedId(role._id)}
            >
              <div className={styles.rowMain}>
                <span className={styles.rowName}>{roleNameForDisplay(role.name, role.template)}</span>
                <span className={styles.rowMeta}>
                  {role.is_system && (
                    <span className={styles.systemBadge}>
                      <Lock size={11} /> System role
                    </span>
                  )}
                  {role.template && !role.is_system && (
                    <span className={styles.templateBadge}>
                      from {TEMPLATE_LABELS[role.template]}
                    </span>
                  )}
                  {role.system_role && (
                    <span className={styles.kindBadge}>
                      {SYSTEM_ROLE_LABELS[role.system_role] ?? role.system_role}
                    </span>
                  )}
                  {role.tenant_id === null && (
                    <span className={styles.scopeBadge}>Global</span>
                  )}
                </span>
              </div>
              <span className={styles.rowAside}>
                {role.assignment_count} user{role.assignment_count === 1 ? '' : 's'} · {role.permissions.length} resources
              </span>
            </button>
          ))}
        </div>
      )}

      {createModalOpen && (
        <CreateRoleModal
          onClose={() => setCreateModalOpen(false)}
          onCreated={(role) => {
            setCreateModalOpen(false);
            setSelectedId(role._id);
          }}
        />
      )}

      <p className={styles.unused}>{onNavigate ? '' : ''}</p>
    </div>
  );
}

// ── Detail view: matrix grid editor ─────────────────────────────────

interface RoleDetailProps {
  roleId: string;
  onBack: () => void;
  onDeleted: () => void;
}

function RoleDetail({ roleId, onBack, onDeleted }: RoleDetailProps) {
  const [role, setRole] = useState<RoleView | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [draftName, setDraftName] = useState('');
  const [draftMatrix, setDraftMatrix] = useState<PermissionMatrixEntry[]>([]);

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchRole(roleId)
      .then((r) => {
        setRole(r);
        setDraftName(r.name);
        setDraftMatrix(normalizeMatrix(r.permissions));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [roleId]);

  const isSystem = role?.is_system ?? true; // safe default while loading

  const handleSave = async (): Promise<void> => {
    if (!role || isSystem) return;
    setSaving(true);
    setError('');
    try {
      const updated = await updateRole(role._id, {
        name: draftName.trim(),
        permissions: draftMatrix.filter((row) => row.create || row.read || row.update || row.delete),
      });
      setRole(updated);
      setDraftName(updated.name);
      setDraftMatrix(normalizeMatrix(updated.permissions));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!role || isSystem) return;
    const confirmMsg = role.assignment_count > 0
      ? `Delete role '${roleNameForDisplay(role.name, role.template)}'? It still has ${role.assignment_count} assignment${role.assignment_count === 1 ? '' : 's'}: that will fail unless you reassign first.`
      : `Delete role '${roleNameForDisplay(role.name, role.template)}'? This can't be undone.`;
    if (!confirm(confirmMsg)) return;
    try {
      await deleteRole(role._id);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const toggleCell = (resource: PermissionResource, action: Action): void => {
    if (isSystem) return;
    setDraftMatrix((prev) => prev.map((row) =>
      row.resource === resource ? { ...row, [action]: !row[action] } : row,
    ));
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={onBack}>
          <ArrowLeft size={16} /> Back to Roles
        </button>
        <div className={styles.loading}>Loading…</div>
      </div>
    );
  }

  if (error && !role) {
    return (
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={onBack}>
          <ArrowLeft size={16} /> Back to Roles
        </button>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  if (!role) return null;

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={onBack}>
        <ArrowLeft size={16} /> Back to Roles
      </button>

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><ShieldCheck size={18} /></div>
          <div>
            {isSystem ? (
              <h1 className={styles.title}>{roleNameForDisplay(role.name, role.template)}</h1>
            ) : (
              <input
                className={styles.titleInput}
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                aria-label="Role name"
              />
            )}
            <p className={styles.subtitle}>
              {role.system_role ? SYSTEM_ROLE_LABELS[role.system_role] : 'Custom role'}
              {role.template && ` · from ${TEMPLATE_LABELS[role.template]}`}
              {role.tenant_id === null ? ' · Global' : ''}
              {' · '}
              {role.assignment_count} user{role.assignment_count === 1 ? '' : 's'} assigned
            </p>
          </div>
        </div>
        {isSystem ? (
          <span className={styles.systemBadge}>
            <Lock size={12} /> System role · read-only
          </span>
        ) : (
          <div className={styles.detailActions}>
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={handleDelete}
              disabled={saving}
            >
              <Trash2 size={13} /> Delete
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <><Loader2 size={13} className={styles.spin} /> Saving…</> : <><Save size={13} /> Save changes</>}
            </button>
          </div>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Matrix grid. Resources × CRUD checkboxes. is_system roles
          render the same grid with all checkboxes disabled. */}
      <div className={styles.matrixWrap}>
        <table className={styles.matrix}>
          <thead>
            <tr>
              <th className={styles.matrixCornerCell}>Resource</th>
              {ACTIONS.map((a) => (
                <th key={a} className={styles.matrixHeaderCell}>{a}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {draftMatrix.map((row) => (
              <tr key={row.resource}>
                <td className={styles.matrixResourceCell}>
                  {RESOURCE_LABELS[row.resource] ?? row.resource}
                </td>
                {ACTIONS.map((a) => (
                  <td key={a} className={styles.matrixCell}>
                    <input
                      type="checkbox"
                      className={styles.matrixCheckbox}
                      checked={row[a]}
                      disabled={isSystem || saving}
                      onChange={() => toggleCell(row.resource, a)}
                      aria-label={`${RESOURCE_LABELS[row.resource] ?? row.resource} ${a}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Create modal ────────────────────────────────────────────────────

interface CreateRoleModalProps {
  onClose: () => void;
  onCreated: (role: RoleView) => void;
}

function CreateRoleModal({ onClose, onCreated }: CreateRoleModalProps) {
  const [name, setName] = useState('');
  const [template, setTemplate] = useState<RoleTemplateValue>('building_admin');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const submit = async (): Promise<void> => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const created = await createRole({ name: name.trim(), template });
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
      setCreating(false);
    }
  };

  return (
    <Modal isOpen title="New Role" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Name</label>
          <input
            className={formStyles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Wilson Avenue Admin"
            autoFocus
          />
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Template</label>
          <select
            className={formStyles.input}
            value={template}
            onChange={(e) => setTemplate(e.target.value as RoleTemplateValue)}
          >
            {TEMPLATE_VALUES.map((t) => (
              <option key={t} value={t}>{TEMPLATE_LABELS[t]}</option>
            ))}
          </select>
          <p className={styles.hint}>
            The new role inherits the template&rsquo;s permissions. You can
            customize them after creating.
          </p>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" className={formStyles.btnSecondary} onClick={onClose} disabled={creating}>
            Cancel
          </button>
          <button type="button" className={formStyles.btnPrimary} onClick={submit} disabled={creating}>
            {creating ? <><Loader2 size={13} className={styles.spin} /> Creating…</> : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

/**
 * Expand a stored matrix to a full 17-row matrix, one row per
 * PermissionResource. Resources missing from the stored matrix
 * render as all-false rows so the user can toggle them on without
 * needing an "Add resource" button. The save path filters out
 * all-false rows before sending so the persisted matrix stays
 * compact.
 */
function normalizeMatrix(stored: PermissionMatrixEntry[]): PermissionMatrixEntry[] {
  const byResource = new Map<string, PermissionMatrixEntry>(stored.map((e) => [e.resource, e]));
  return PERMISSION_RESOURCES.map((resource) => byResource.get(resource) ?? {
    resource,
    create: false, read: false, update: false, delete: false,
  });
}
